'use client';

import { API_ROUTES, useApiQuery } from '@shared/api-utils';
import { useLocale } from 'next-intl';
import type { ComponentProps, ReactNode } from 'react';
import { LotteryError } from '../../../lotteries/LotteryError/LotteryError';
import { useLotteryNextDraw } from '../../../lotteries/hooks';
import type { LotteryName } from '../../../lottery-purchases/types';
import { getLotteryConfigPath } from '../../../tippengine/services/lotteryConfig';
import type { LotteryConfig } from '../../../tippengine/types';
import type { NextDrawItem } from '../../components/NextDraw/types';
import { DEFAULT_AMOUNT_DRAWS_TO_SHOW, type DrawingsResponse, type ResultsConfig } from '../../types';
import { getLatestResultsQueryOptions } from '../../utils';
import { ResultsLatestPage } from '../ResultsLatestPage/ResultsLatestPage';

type ResultsPageClientProps = ComponentProps<'div'> & {
  lotteryName: LotteryName;
  config?: ResultsConfig;
  backFallbackUrl?: string;
  pageHeaderActions?: ReactNode;
};

function ResultsPageClient({ lotteryName, config, backFallbackUrl, children, pageHeaderActions, ...props }: ResultsPageClientProps) {
  const locale = useLocale();
  const { data: lotteryConfig, isLoading: isLotteryConfigLoading } = useApiQuery<Pick<LotteryConfig, 'amountDrawsToShow'>>(
    getLotteryConfigPath(lotteryName, locale.replace('-', '_'))
  );
  const amountDrawsToShow = lotteryConfig?.amountDrawsToShow ?? DEFAULT_AMOUNT_DRAWS_TO_SHOW;
  const resultsLimit = isLotteryConfigLoading ? undefined : amountDrawsToShow;
  const { data: nextDraws } = useLotteryNextDraw(lotteryName);
  const nextDraw = nextDraws?.find((draw: NextDrawItem) => draw.lotteryName === lotteryName);
  const { data: results, isLoading: isResultsLoading } = useApiQuery<DrawingsResponse>(
    API_ROUTES.LOTTERIES.LOTTERY_RESULTS.replace(':lotteryName', lotteryName),
    getLatestResultsQueryOptions(resultsLimit, nextDraw?.drawingDate, config?.revalidateOffsetMs, nextDraw?.id?.regular, nextDraw?.closingDate?.regular)
  );

  if (!isLotteryConfigLoading && !isResultsLoading && !results) return <LotteryError lotteryName={lotteryName} />;

  return (
    <ResultsLatestPage
      data={results ?? []}
      lotteryName={lotteryName}
      amountDrawsToShow={amountDrawsToShow}
      config={config}
      backFallbackUrl={backFallbackUrl}
      pageHeaderActions={pageHeaderActions}
      {...props}
    >
      {children}
    </ResultsLatestPage>
  );
}

export { ResultsPageClient, type ResultsPageClientProps };
